import { ItemSheetData } from "@/types/itemsheet";
import { BidSummary, BuyerGroupingTable, Scenario } from "@/types/scenario";

/**
 * buyer 이름에서 반복되는 " C" suffix를 제거하고 base name을 반환
 * "GSD C C C C" → "GSD"
 * "GSD C" → "GSD"
 * "GSD" → "GSD"
 */
export const getBaseBuyerName = (buyer: string): string => {
  return buyer.replace(/( C)+$/, "");
};

/**
 * Custom Price 그룹의 이름을 생성 (항상 baseName + " C" 형태)
 * "GSD C C C C" → "GSD C"
 * "GSD" → "GSD C"
 */
export const getCustomGroupName = (buyer: string): string => {
  return `${getBaseBuyerName(buyer)} C`;
};

export function calculateGroupSummary(
  items: ItemSheetData[],
  discountRate: number
) {
  let totalListPrice = 0;
  let totalCost = 0;

  for (const item of items) {
    const quantity = item.quantity ?? 0;
    const listPrice = item.list_price ?? 0;
    totalListPrice += quantity * listPrice;
    totalCost += item.total_cost ?? 0;
  }

  const supplyPrice = totalListPrice * (1 - discountRate / 100);

  const margin = supplyPrice - totalCost;
  const marginRate = supplyPrice > 0 ? (margin / supplyPrice) * 100 : 0;

  return {
    totalListPrice: Math.round(totalListPrice),
    discountRate,
    supplyPrice: Math.round(supplyPrice),
    totalCost: Math.round(totalCost),
    margin: Math.round(margin),
    marginRate,
  };
}

export function convertScenarioToGroupingTable(
  data: Scenario["data"]
): BuyerGroupingTable[] {
  // 저장된 시나리오 데이터의 그룹 배치를 그대로 유지
  // isCustomPrice 기반으로 재분리하지 않음 (사용자가 이동한 결과를 존중)
  // " C C C" 같은 중복 suffix만 정규화
  const normalizedGroups = new Map<string, BuyerGroupingTable>();

  Object.entries(data).forEach(([buyer, { discountRate, items = [] }]) => {
    const baseName = getBaseBuyerName(buyer);
    const isCustomGroup = buyer !== baseName;
    const normalizedBuyer = isCustomGroup ? getCustomGroupName(buyer) : buyer;

    const existing = normalizedGroups.get(normalizedBuyer);
    if (existing) {
      existing.items.push(...items);
      existing.summary = calculateGroupSummary(existing.items, existing.discountRate);
    } else {
      normalizedGroups.set(normalizedBuyer, {
        buyer: normalizedBuyer,
        items: [...items],
        discountRate,
        summary: calculateGroupSummary(items, discountRate),
        isCustomGroup,
      });
    }
  });

  return Array.from(normalizedGroups.values());
}

export function calculateScenarioBidSummary({
  scenarioData,
  budgetAmountMap,
}: {
  scenarioData: Scenario["data"];
  budgetAmountMap: { budget: number; basisAmount: string | undefined };
}): BidSummary {
  const groupList = convertScenarioToGroupingTable(scenarioData);

  const bidAmount =
    groupList.reduce((acc, { summary }) => acc + summary.supplyPrice, 0) * 1.1;

  const baseAmount = budgetAmountMap.basisAmount
    ? Number(budgetAmountMap.basisAmount)
    : budgetAmountMap.budget;
  const bidRate = baseAmount > 0 ? (bidAmount / baseAmount) * 100 : 0;

  return {
    groupList,
    bidAmount: Math.round(bidAmount),
    baseAmountMap: budgetAmountMap,
    bidRate,
  };
}

export const formatScenarioRequestData = (divisions: BuyerGroupingTable[]) => {
  console.log("formatScenarioRequestData - input divisions:", divisions);
  const result = divisions.reduce(
    (acc, curr) => {
      acc[curr.buyer] = {
        discountRate: curr.discountRate,
        items: curr.items,
      };
      return acc;
    },
    {} as Record<
      string,
      {
        discountRate: number;
        items: ItemSheetData[];
      }
    >
  );
  console.log("formatScenarioRequestData - result:", result);
  return result;
};

export const getNextScenarioTitle = (scenarios: Scenario[]) => {
  const nums = scenarios
    .map(({ title }) => {
      const match = title.match(/^시나리오 (\d+)$/);
      return match ? parseInt(match[1], 10) : null;
    })
    .filter((num): num is number => num !== null);

  const maxNum = nums.length > 0 ? Math.max(...nums) : 0;
  return `시나리오 ${maxNum + 1}`;
};
